import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Typography, message as antMessage } from 'antd';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useQuotesContext from './QuotesContext';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import useHandleResize from '../../utils/HandleResize';

const docList = ['ipid', 'cgv'];

const InfosBar = ({ contractType, showRestrictionButton }) => {
  const { t } = useTranslation();
  const { subPath: type } = useParams();
  const navigate = useNavigate();
  const { dispatchAPI, getProductAccess } = useAuthContext();
  const { message } = useErrorMessage();
  const { displayRestricted, setDisplayRestricted } = useQuotesContext();
  const [documents, setDocuments] = useState({});
  const { width } = useHandleResize();
  const displayAuthorization =
    getProductAccess(type)?.advancedManagement?.blocked_fit?.selected;

  const isReductScreen = width < 1400;
  const isXSScreen = width < 830;

  const gridStyleButton = {
    width: isXSScreen ? '30%' : '25%',
    height: '60px',
    padding: isReductScreen ? '9px 4px 9px 35px' : '9px 9px 9px 35px',
    display: 'flex',
    alignItems: 'center'
  };

  const gridStyle = {
    width: isXSScreen ? '60%' : '70%',
    height: '60px',
    padding: isReductScreen ? '9px 24px 9px 4px' : '9px 24px 9px 9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  };

  const getProductDocuments = useCallback(async () => {
    try {
      let url;
      if (contractType) {
        url = `/products/documents/${contractType}`;
      }
      if (type) {
        url = `/products/documents/${type}`;
      }
      const { data } = await dispatchAPI('GET', {
        url
      });
      setDocuments(data);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    // eslint-disable-next-line
  }, [type, contractType]);

  useEffect(() => {
    (async () => {
      if (contractType || type) {
        await getProductDocuments();
      }
      if (type && displayAuthorization) {
        setDisplayRestricted(true);
      } else if (type) {
        setDisplayRestricted(false);
      }
    })();
    // eslint-disable-next-line
  }, [getProductDocuments]);

  const downloadDocument = async (docs) => {
    if (!Array.isArray(docs) || docs.length === 0) {
      return;
    }

    if (docs.length > 1) {
      const productType = contractType;
      const uppercasedType = productType.toUpperCase();
      navigate(`/documents?type=${uppercasedType}`);
      return;
    }

    try {
      const {
        _id,
        metadata: { originalName }
      } = docs[0];
      const { data } = await dispatchAPI('GET', {
        url: `/products/files/${_id}`,
        responseType: 'blob'
      });
      const blob = new Blob([data], {
        type: data?.type
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalName;
      a.click();
      antMessage.success(t('export.messages.success'));
    } catch (e) {
      if (e.response) message(e.response.status);
    }
  };

  return (
    <>
      <Card bordered={false}>
        {showRestrictionButton && (
          <Card.Grid hoverable={false} style={gridStyleButton}>
            <Typography.Text style={{ color: 'var(--textColorSecondary)' }}>
              Affichage
            </Typography.Text>
            <Button
              onClick={() => setDisplayRestricted(!displayRestricted)}
              style={{
                padding: isReductScreen
                  ? '1px 8px 4px 8px'
                  : '0px 15px 3px 15px',
                marginLeft: 4
              }}
            >
              {displayRestricted ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              <span
                style={{
                  display: isReductScreen ? 'none' : 'inline-block',
                  marginLeft: 8
                }}
              >
                {displayRestricted ? 'Restreint' : 'Complet'}
              </span>
            </Button>
          </Card.Grid>
        )}
        <Card.Grid hoverable={false} style={gridStyle}>
          {docList.map((doc) => (
            <Button
              key={doc}
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => downloadDocument(documents[doc])}
              disabled={!documents[doc] || documents[doc]?.length === 0}
            >
              <span
                style={{
                  marginLeft: 8,
                  display: isXSScreen ? 'inline' : 'none'
                }}
              >
                {t(`contracts.documents.${doc}.reduced`)}
              </span>
              <span
                style={{
                  marginLeft: 8,
                  display: isXSScreen ? 'none' : 'inline'
                }}
              >
                {t(`contracts.documents.${doc}.full`)}
              </span>
            </Button>
          ))}
        </Card.Grid>
      </Card>
    </>
  );
};

InfosBar.propTypes = {
  contractType: PropTypes.string,
  showRestrictionButton: PropTypes.bool
};

InfosBar.defaultProps = {
  contractType: undefined,
  showRestrictionButton: true
};

export default InfosBar;
